import * as React from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { itIT } from '@mui/x-data-grid/locales';

/**
 * TableSnapshotAffissioni – compatibile con NUOVO endpoint snapshot
 *
 * Props supportate:
 * - branch: oggetto di snapshot (es: snapshot.manager OR snapshot.admin)
 *           deve avere: { dettaglioPerLuogo: [...], totalOffices?, managed?, ... }
 * - val:    array legacy (back-compat) con { denominazione, completati, inProgress, nonAvviati, totale }
 * - campaignName: string per filename CSV
 * - viewType: 'manager' | 'admin' (solo per naming CSV)
 *
 * Logica righe:
 * - Se item ha i campi numerici (legacy) li usa direttamente.
 * - Se item ha solo `stato` (completed | in progress | not started), converte in 1/0 e total = 1.
 */

const paginationModel = { page: 0, pageSize: 15 };

function TableSnapshotAffissioni({ branch, val, campaignName, viewType = 'manager' }) {
  // Sorgente dati: priorità al nuovo schema (branch.dettaglioPerLuogo), fallback a val
  const dettaglio = React.useMemo(() => {
    if (Array.isArray(branch?.dettaglioPerLuogo)) return branch.dettaglioPerLuogo;
    if (Array.isArray(val)) return val;
    return [];
  }, [branch, val]);

  const columns = React.useMemo(() => [
    { field: 'id', headerName: 'RowID', width: 80 },
    { field: 'denominazione', headerName: 'Ufficio', width: 320, flex: 1, minWidth: 220 },
    {
      field: 'completed', headerName: 'Completati', width: 140, headerAlign: 'center', align: 'center',
      renderCell: ({ value }) => (
        <Box sx={{ backgroundColor: '#d0f2d5', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {value}
        </Box>
      ),
    },
    {
      field: 'inProgress', headerName: 'In Progress', width: 140, headerAlign: 'center', align: 'center',
      renderCell: ({ value }) => (
        <Box sx={{ backgroundColor: '#fff6c2', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {value}
        </Box>
      ),
    },
    {
      field: 'nonAvviati', headerName: 'Non Avviati', width: 140, headerAlign: 'center', align: 'center',
      renderCell: ({ value }) => (
        <Box sx={{ backgroundColor: '#fddede', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {value}
        </Box>
      ),
    },
    {
      field: 'total', headerName: 'Totale', width: 110, headerAlign: 'center', align: 'center',
      renderCell: ({ value }) => (
        <Box sx={{ backgroundColor: '#e6f22694', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {value}
        </Box>
      ),
    },
    {
      field: 'percentage', headerName: '% Completamento', width: 180, headerAlign: 'center', align: 'center',
      valueFormatter: (params) =>
        typeof params.value === 'number' ? `${(params.value * 100).toFixed(1)}%` : '—',
    },
    {
      field: 'media', headerName: 'Media', width: 120, headerAlign: 'center', align: 'center',
      renderCell: ({ value }) => (
        <Box sx={{ backgroundColor: '#45f22694', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {typeof value === 'number' ? value.toFixed(2) : '—'}
        </Box>
      ),
    },
  ], []);

  const rows = React.useMemo(() => {
    return dettaglio.map((item, index) => {
      const hasNumeric = (
        typeof item?.completati === 'number' ||
        typeof item?.inProgress === 'number' ||
        typeof item?.nonAvviati === 'number' ||
        typeof item?.totale === 'number'
      );

      const completed = hasNumeric ? (item.completati ?? 0) : (item.stato === 'completed' ? 1 : 0);
      const inProgress = hasNumeric ? (item.inProgress ?? 0) : (item.stato === 'in progress' ? 1 : 0);
      const nonAvviati = hasNumeric ? (item.nonAvviati ?? 0) : (item.stato === 'not started' ? 1 : 0);
      const total = hasNumeric ? (item.totale ?? (completed + inProgress + nonAvviati)) : 1;

      return {
        id: index,
        denominazione: item.denominazione || 'N/D',
        completed,
        inProgress,
        nonAvviati,
        total,
        percentage: total > 0 ? (completed / total) : null,
        media: total > 0 ? (completed * 100 / total) : null,
      };
    });
  }, [dettaglio]);

  const fileName = React.useMemo(() => {
    const roleTag = viewType === 'admin' ? 'Admin' : 'Manager';
    const base = `Affissioni-${roleTag}-${campaignName?.replace(/\s+/g, '_') || 'Campagna'}`;
    return base;
  }, [viewType, campaignName]);

  return (
    <Paper sx={{ width: '100%' }}>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        initialState={{
          pagination: { paginationModel },
          columns: {
            columnVisibilityModel: {
              denominazione: true,
              completed: true,
              inProgress: true,
              nonAvviati: true,
              total: true,
              percentage: true,
              media: true,
            },
          },
        }}
        pageSizeOptions={[5, 10, 15, 25, 50, 100]}
        density="compact"
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            csvOptions: { fileName },
          },
        }}
        sx={{ border: 0 }}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
      />
    </Paper>
  );
}

export default TableSnapshotAffissioni;
